import api from './api';
import type { TradingSymbol, Candle, Timeframe, InstrumentType, SupportedCatalogueType, MarketProvider } from '@trading/shared';
import { FOREX_SYMBOLS, CRYPTO_SYMBOLS } from '@trading/shared';

// ---------------------------------------------------------------------------
// Client-side candle cache — avoids redundant API calls when switching
// between symbols/timeframes quickly (e.g. toggling back and forth).
// Only caches "latest" requests (no `before` param). TTL 10s, max 20 entries.
// ---------------------------------------------------------------------------
const CANDLE_CACHE_TTL_MS = 10_000;
const CANDLE_CACHE_MAX = 20;

interface CacheEntry {
  result: CandlesResult;
  ts: number;
}

const candleCache = new Map<string, CacheEntry>();

function getCachedCandles(key: string): CandlesResult | null {
  const entry = candleCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CANDLE_CACHE_TTL_MS) {
    candleCache.delete(key);
    return null;
  }
  // Move to end so LRU eviction works (Map iterates in insertion order)
  candleCache.delete(key);
  candleCache.set(key, entry);
  return entry.result;
}

function setCachedCandles(key: string, result: CandlesResult): void {
  // Delete first so re-insert refreshes position for LRU ordering
  candleCache.delete(key);
  if (candleCache.size >= CANDLE_CACHE_MAX) {
    const lru = candleCache.keys().next().value;
    if (lru !== undefined) candleCache.delete(lru);
  }
  candleCache.set(key, { result, ts: Date.now() });
}

/** Visible for testing only. */
export function _resetCandleCache(): void {
  candleCache.clear();
}

interface CandlesResponse {
  symbol: string;
  timeframe: string;
  candles: Candle[];
  /** Provider that actually served the data — used to gate live tick application. */
  resolvedProvider: MarketProvider;
}

export interface CandlesResult {
  candles: Candle[];
  resolvedProvider: MarketProvider;
}

export interface HistoricalPriceResponse {
  price: number;
  source: string;
  note: string;
}

interface JournalSymbolsResponse {
  recent: string[];
  suggestions: { symbol: string; name: string }[];
}

export const marketService = {
  async getSymbols(): Promise<TradingSymbol[]> {
    const response = await api.get<TradingSymbol[]>('/market/symbols');
    return response.data;
  },

  async getCandles(
    symbol: string,
    timeframe: Timeframe,
    limit = 100,
    before?: number,
    signal?: AbortSignal,
    provider?: MarketProvider,
  ): Promise<CandlesResult> {
    // Only cache "latest" requests — historical load-more always fetches fresh.
    // Include provider in key to prevent cross-provider cache collisions.
    const cacheKey = before == null ? `${symbol}:${timeframe}:${limit}:${provider ?? 'auto'}` : null;
    if (cacheKey) {
      const cached = getCachedCandles(cacheKey);
      if (cached) return cached;
    }

    const response = await api.get<CandlesResponse>(
      `/market/candles/${symbol}`,
      { params: { timeframe, limit, before, ...(provider ? { provider } : {}) }, signal }
    );
    const result: CandlesResult = {
      candles: response.data.candles,
      resolvedProvider: response.data.resolvedProvider,
    };

    if (cacheKey && !signal?.aborted) setCachedCandles(cacheKey, result);
    return result;
  },

  async getHistoricalPrice(
    symbol: string,
    timestamp: string,
    instrumentType: InstrumentType
  ): Promise<HistoricalPriceResponse> {
    const response = await api.get<HistoricalPriceResponse>('/market/price', {
      params: { symbol, timestamp, instrumentType },
    });
    return response.data;
  },

  async getJournalSymbols(
    instrumentType: SupportedCatalogueType,
    query: string,
    limit?: number,
    signal?: AbortSignal,
  ): Promise<JournalSymbolsResponse> {
    const response = await api.get<JournalSymbolsResponse>(
      '/market/symbols/search',
      { params: { instrumentType, query, limit }, signal },
    );
    return response.data;
  },

  async searchAnalysisSymbols(
    q: string,
    limit = 20,
    signal?: AbortSignal,
  ): Promise<TradingSymbol[]> {
    const response = await api.get<TradingSymbol[]>(
      '/market/symbols/search-analysis',
      { params: { q, limit }, signal },
    );
    return response.data;
  },
};

/**
 * Client-side static fallback for FOREX and CRYPTO when the catalogue API is
 * unreachable (offline or unauthenticated). STOCK, FUTURE, and OPTION have no
 * client-side static data, so they return an empty array.
 */
export function getStaticSymbolFallback(
  instrumentType: InstrumentType,
  query: string,
  limit: number,
): { symbol: string; name: string }[] {
  const pool: readonly { name: string; displayName: string }[] =
    instrumentType === 'FOREX' ? FOREX_SYMBOLS
    : instrumentType === 'CRYPTO' ? CRYPTO_SYMBOLS
    : [];

  if (pool.length === 0) return [];

  const q = query.toUpperCase();
  return pool
    .filter((s) => !q || s.name.toUpperCase().includes(q) || s.displayName.toUpperCase().includes(q))
    .slice(0, limit)
    .map((s) => ({ symbol: s.name, name: s.displayName }));
}
